import React, { useMemo } from 'react';

import parse, { domToReact } from 'html-react-parser';
import styled from 'styled-components';
import { get } from 'lodash';
import { Show } from '../../show';

import GridContainer from '../../layouts/components/grid-container';
import FluidContainer from '../../layouts/components/fluid-container';

import { Question, PublicTitle, Attachments } from '../components';
import AudioPlayer from '../../audio-player';
import ImageGallery from '../../image-gallery';
import SharedText from '../../shared-text';
import Textarea from '../../textarea';
import YouTubePlayer from '../../video-player/youtube';
import { exerciseDefaultProps, exercisePropTypes } from '../../../constants/exercise';
import { deleteHtmlTagsFromString, parseElement, parseTextareaAnswer } from '../../../lib/exercise';
import Geogebra from '../../geogebra';
import HintBox from '../../hint-box';
import Stack from '../../stack';
import MathContent from '../../math-content';
import { parseMfenced } from '../../../lib/utils';

export const TextareaContainer = styled.div`
    display: inline;
`;

const Link = styled.a`
    color: var(--blue500);
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

function saveValueToStatus(content, id, onAnswer, answers) {
    const filteredAnswers = answers.filter((item) => item.gapId !== content.gapId);
    if (content.value !== '' && /\S/.test(content.value)) {
        onAnswer(id, [...filteredAnswers, content]);
    } else {
        onAnswer(id, [...filteredAnswers]);
    }
}

function handleValidationType(id, answers, options) {
    const insertedValue = get(
        answers.find((item) => item.gapId === id),
        'value',
    );
    if (insertedValue) {
        const correctValues = get(
            options.find((item) => item.id === id),
            'values',
        );
        const parsedCorrectValues = correctValues.map((item) => parseTextareaAnswer(deleteHtmlTagsFromString(item).trim()));
        if (parsedCorrectValues.includes(parseTextareaAnswer(deleteHtmlTagsFromString(insertedValue)))) {
            return 'success';
        } else {
            return 'error';
        }
    } else {
        return 'missing';
    }
}

const FillBlanks = ({
    exercise,
    answers = exerciseDefaultProps.answers,
    onAnswer,
    correctionStatus,
    onPlayVideo,
    videos,
    show,
    hasAttachments = exerciseDefaultProps.hasAttachments,
    hintBoxIsOpen,
    toggleHintBox,
}) => {
    let question = exercise.question.replace(/<gap_ref (.*?)\/>/g, '<gap_ref $1></gap_ref>');
    const showGrid = (hasAttachments.images || hasAttachments.video) && show.attachments;

    const parsedGaps = useMemo(() => parseElement(exercise.question, exercise.options), [exercise]);

    const options = {
        replace: (domNode) => {
            if (domNode.name && domNode.name === 'mfenced') {
                return parseMfenced(domNode, options);
            }

            if (domNode.name && domNode.name === 'gap_ref') {
                const gapId = domNode.attribs.idref.replace('_', '');
                const parsedGap = parsedGaps.get(domNode.attribs.idref);

                const validationString = handleValidationType(gapId, answers, exercise.options);

                return correctionStatus ? (
                    <TextareaContainer>
                        <Textarea
                            key={`${exercise.id}_${gapId}`}
                            badgeIndex={parsedGap.char}
                            id={gapId}
                            isValidated={true}
                            value={
                                validationString === 'missing'
                                    ? exercise.options.find((item) => item.id === gapId).values[0]
                                    : get(
                                          answers.find((item) => item.gapId === gapId),
                                          'value',
                                      )
                            }
                            validationType={validationString}
                        />
                    </TextareaContainer>
                ) : (
                    <TextareaContainer>
                        <Textarea
                            key={`${exercise.id}_${gapId}`}
                            value={get(
                                answers.find((item) => item.gapId === gapId),
                                'value',
                            )}
                            id={gapId}
                            disabled={false}
                            isValidated={false}
                            onChange={(text) => saveValueToStatus({ value: text.trim(), gapId: gapId }, exercise.id, onAnswer, answers)}
                        />
                    </TextareaContainer>
                );
            }
        },
    };

    let parsedQuestion = parse(question, options);

    const hintBoxLabel = (
        <span className="body-5">
            <span className="body-5-sb">{'Per inserire vocali accentate o caratteri speciali'}</span>
            {
                ' tieni premuto il tasto della lettera e scegli la variante che ti serve. Fai attenzione: le lettere maiuscole o minuscole e la punteggiatura possono fare la differenza.'
            }
            <br />
            <span className="body-5-sb">{'Per scrivere in caratteri non latini'}</span>
            {' aggiungi sul tuo dispositivo la tastiera nella lingua di cui hai bisogno.'}
            <Link
                target="_blank"
                href="https://assistenza.zanichelli.it/support/solutions/articles/4000184467-come-faccio-a-inserire-caratteri-non-latini-negli-esercizi-su-zte-"
                rel="noreferrer"
            >
                {' Leggi come fare nelle Faq.'}
            </Link>
        </span>
    );

    return (
        <>
            <Show when={show.title && exercise.publicTitle}>
                <FluidContainer paddingBottom={24}>
                    <PublicTitle>{exercise.publicTitle}</PublicTitle>
                </FluidContainer>
            </Show>

            <Stack direction="vertical" gap="small">
                <Show when={show.prompt && exercise.prompt}>
                    <FluidContainer>
                        <MathContent>{exercise.prompt}</MathContent>
                    </FluidContainer>
                </Show>

                <Show when={show.hintBox}>
                    <HintBox label={hintBoxLabel} isOpen={hintBoxIsOpen} exerciseType={exercise.typeLabel} toggleHintBox={toggleHintBox} />
                </Show>
            </Stack>

            <Show when={show.attachments && hasAttachments.geogebra}>
                <FluidContainer paddingBottom={24}>
                    <Geogebra exerciseId={exercise.id} src={exercise.attachments.geogebra?.src} />
                </FluidContainer>
            </Show>

            <Show when={show.sharedText && hasAttachments.sharedText}>
                <FluidContainer paddingBottom={24} flexWrap="wrap">
                    <SharedText exercise={exercise} />
                </FluidContainer>
            </Show>

            {show.attachments && hasAttachments.audio && (
                <FluidContainer paddingBottom={24}>
                    <AudioPlayer source={exercise.attachments.audio?.src} />
                </FluidContainer>
            )}

            <GridContainer columns={[1, showGrid ? 2 : 1]} paddingBottom={24}>
                <Attachments show={showGrid}>
                    <Stack gap="desktop">
                        {hasAttachments.video && (
                            <YouTubePlayer
                                videoIdOrSource={exercise.attachments.video?.src}
                                onPlayVideo={() => onPlayVideo(exercise.id)}
                                showPreview={!videos.find((item) => item.exerciseId === exercise.id)}
                            />
                        )}
                        {hasAttachments.images && <ImageGallery images={get(exercise, 'attachments.images')} />}
                    </Stack>
                </Attachments>
                <Question>{parsedQuestion}</Question>
            </GridContainer>
        </>
    );
};

FillBlanks.propTypes = exercisePropTypes;

export default FillBlanks;
